import React, { type ChangeEvent } from "react";
import { Controller } from "react-hook-form";
import Disclaimer from "../../../../../../../Common/Disclaimer";
import { Checkbox, Label } from "../../../../../../../Inputs";
import { Text } from "../../../../../../../Inputs/react-hook-form";
import type { PathArray } from "../../../FieldPathContext/FieldPathContext";
import { getFieldName } from "../../../FieldPathContext/path-utils";
import {
  getSchemaPropertyFormPath,
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
} from "../../../form";
import { getIsFieldRequired, getRequiredPropertyFormPath } from "../required";

type FieldSettingsConfigProps = {
  fieldPath: PathArray;
  isFormulaField?: boolean;
  isLabelRequired?: boolean;
  isModuleInput?: boolean;
  isRequiredFieldDisabled?: boolean;
  isReadOnly?: boolean;
  onRequiredChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

export function FieldSettingsConfig({
  fieldPath,
  isFormulaField,
  isLabelRequired = true,
  isModuleInput,
  isRequiredFieldDisabled,
  isReadOnly,
  onRequiredChange,
}: FieldSettingsConfigProps) {
  const { register, getFieldState } = useBuilderFormContext();

  const fieldLabelPropertyFormPath = getSchemaPropertyFormPath(
    fieldPath,
    "title",
  );
  const fieldLabelError = getFieldState(fieldLabelPropertyFormPath)?.error;

  const tooltipTextPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "tooltipText",
  );

  return (
    <>
      {isFormulaField && (
        <Disclaimer
          message="This field is derived from a formula and cannot be modified directly. You may still customize supporting text, default values, and permissions."
          margin={false}
        />
      )}
      <div>
        <Label
          text="Field label"
          htmlFor={fieldLabelPropertyFormPath}
          required={isLabelRequired}
        />
        <Text
          {...register(fieldLabelPropertyFormPath, {
            required: isLabelRequired ? "Field label is required" : undefined,
          })}
          error={fieldLabelError?.message}
          disabled={isReadOnly}
        />
      </div>
      {!isFormulaField && (
        <RequiredFieldCheckbox
          fieldPath={fieldPath}
          disabled={isReadOnly || isRequiredFieldDisabled || isModuleInput}
          onRequiredChange={onRequiredChange}
        />
      )}
      <div>
        <Label text="Tooltip text" htmlFor={tooltipTextPropertyFormPath} />
        <Text
          {...register(tooltipTextPropertyFormPath)}
          disabled={isReadOnly}
        />
      </div>
    </>
  );
}

type RequiredFieldCheckboxProps = {
  fieldPath: PathArray;
  disabled?: boolean;
  onRequiredChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

function RequiredFieldCheckbox({
  fieldPath,
  disabled,
  onRequiredChange,
}: RequiredFieldCheckboxProps) {
  const fieldName = getFieldName(fieldPath);
  const requiredPropertyFormPath = getRequiredPropertyFormPath(fieldPath);
  const { control } = useBuilderFormContext();

  return (
    <Controller
      control={control}
      name={requiredPropertyFormPath}
      render={({ field }) => {
        const fieldValue = (field.value as Array<string>) ?? [];

        return (
          <Checkbox
            label="Required field"
            value={getIsFieldRequired(fieldName, fieldValue)}
            disabled={disabled}
            additionalOnChange={onRequiredChange}
            onChange={value => {
              field.onChange(
                value ?
                  [...new Set([...fieldValue, fieldName].filter(Boolean))]
                : fieldValue.filter(name => name !== fieldName),
              );
            }}
          />
        );
      }}
    />
  );
}
